import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import ButtonBase from '@material-ui/core/ButtonBase';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';

import Icon from '@mdi/react';

import { ROUNDED_CONTROL_RADIUS_SMALL } from 'components/theme/controlRadius';
import { getRedesignDialogColors } from './redesignDialogColors';
import { getRedesignDialogFocusRing } from './redesignDialogStyles';

/** The button's square side. */
export const REDESIGN_DIALOG_ICON_BUTTON_SIZE = 36;

// Every rule under '&&': ButtonBase's root and the themes' MuiButtonBase
// override (white text in all three themes) are one class each.
const useStyles = makeStyles(
  (theme) => {
    const colors = getRedesignDialogColors(theme);
    return {
      root: {
        '&&': {
          width: REDESIGN_DIALOG_ICON_BUTTON_SIZE,
          height: REDESIGN_DIALOG_ICON_BUTTON_SIZE,
          flexShrink: 0,
          borderRadius: ROUNDED_CONTROL_RADIUS_SMALL,
          color: colors.muted,
          transition: theme.transitions.create(['background-color', 'color'], {
            duration: theme.transitions.duration.shortest,
          }),
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: colors.hoverFill,
              color: colors.text,
            },
          },
        },
      },
      // A toggle that is on (a PDF toolbar's fit-to-width, say).
      selected: {
        '&&': {
          backgroundColor: colors.selectedFill,
          color: colors.text,
        },
      },
      disabled: {
        '&&': {
          color: colors.disabled,
        },
      },
      focusVisible: {
        '&&': getRedesignDialogFocusRing(colors),
      },
    };
  },
  { name: 'LixRedesignDialogIconButton' }
);

/**
 * A square icon button of the redesigned dialog (the close button, header
 * actions, a PDF pane's toolbar): 36px, the small control corner, a muted
 * glyph that takes the text colour and the hover fill under the pointer, the
 * accent ring on keyboard focus. `aria-label` is required: it names the
 * button and is its tooltip (`tooltip` to word the tooltip otherwise, `null`
 * for none; a disabled button has none). With `selected` it is a toggle
 * (aria-pressed). Other props (onClick, data-*, …) go to the ButtonBase.
 * @example
 * <RedesignDialogIconButton
 *   icon={mdiDownload}
 *   aria-label={t('common.download')}
 *   onClick={handleDownload}
 * />
 * @param {Object} props
 * @param {string} [props.icon] the glyph, an @mdi/js path
 * @param {React.ReactNode} [props.children] a glyph of another kind
 * @param {string} props.aria-label
 * @param {React.ReactNode} [props.tooltip]
 * @param {boolean} [props.selected]
 * @param {boolean} [props.disabled]
 * @param {number} [props.iconSize=0.875] the mdi size (1 = 24px)
 */
const RedesignDialogIconButton = React.forwardRef(
  function RedesignDialogIconButton(props, ref) {
    const {
      icon,
      children,
      'aria-label': label,
      tooltip,
      selected,
      disabled,
      iconSize,
      className,
      ...rest
    } = props;
    const classes = useStyles();
    const button = (
      <ButtonBase
        ref={ref}
        aria-label={label}
        aria-pressed={selected === undefined ? undefined : Boolean(selected)}
        disabled={disabled}
        focusVisibleClassName={classes.focusVisible}
        className={clsx(
          classes.root,
          selected && classes.selected,
          disabled && classes.disabled,
          className
        )}
        data-redesign-dialog-icon-button="true"
        {...rest}
      >
        {icon ? <Icon path={icon} size={iconSize} /> : children}
      </ButtonBase>
    );
    if (disabled || tooltip === null) return button;
    return <Tooltip title={tooltip || label}>{button}</Tooltip>;
  }
);

RedesignDialogIconButton.propTypes = {
  /** The glyph, an @mdi/js path. */
  icon: PropTypes.string,
  /** A glyph of another kind, when there is no `icon`. */
  children: PropTypes.node,
  /** The button's name, and its tooltip unless `tooltip` says otherwise. */
  'aria-label': PropTypes.string.isRequired,
  /** The tooltip's text; `null` for no tooltip. */
  tooltip: PropTypes.node,
  /** On, for a toggle (aria-pressed); leave undefined for a plain button. */
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  /** The mdi size of `icon` (1 = 24px). */
  iconSize: PropTypes.number,
  className: PropTypes.string,
};

RedesignDialogIconButton.defaultProps = {
  iconSize: 0.875,
};

export default RedesignDialogIconButton;
